import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { adminLogin } from '../pages/Login/login.service';
import { LoginRequest } from '../pages/Login/login.dto';

interface Admin {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  admin: Admin | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_token');
      const adminData = localStorage.getItem('admin_data');

      console.log('AuthContext: Checking auth status');
      console.log('AuthContext: Token exists:', !!token);
      console.log('AuthContext: Admin data exists:', !!adminData);

      if (token && adminData) {
        try {
          const parsedAdmin = JSON.parse(adminData);
          console.log('AuthContext: Parsed admin data:', parsedAdmin);
          setAdmin(parsedAdmin);
          setToken(token);
          setIsAuthenticated(true);
          console.log('AuthContext: Authentication successful');
        } catch (error) {
          console.error('Error parsing admin data:', error);
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_data');
        }
      } else {
        console.log('AuthContext: No token or admin data found');
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);

      console.log('AuthContext: Attempting login with API');
      console.log('AuthContext: Login endpoint:', '/admin/auth/login');
      console.log('AuthContext: Credentials:', {
        email: credentials.email,
        password: '***',
      });

      const response = await adminLogin(credentials);
      localStorage.setItem('admin_token', response.access_token);
      localStorage.setItem('admin_data', JSON.stringify(response.admin));
      console.log('AuthContext: Token stored in localStorage');
      console.log('AuthContext: Admin data stored in localStorage');

      setAdmin(response.admin);
      setToken(response.access_token);
      setIsAuthenticated(true);

      console.log('AuthContext: State updated - isAuthenticated: true');
    } catch (error) {
      console.error('AuthContext: Login failed', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('AuthContext: Logout called');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    setAdmin(null);
    setToken(null);
    setIsAuthenticated(false);
    setError(null);
  };

  const value = {
    isAuthenticated,
    admin,
    token,
    login,
    logout,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
